/**
 * Web Audio plumbing: context lifecycle plus bus/gain staging. All actual
 * synthesis lives in sfx.ts and chirps.ts; this only owns the master chain
 * (buses -> masterGain -> compressor -> destination) and the unlock dance.
 *
 * The context is created suspended in the constructor (autoplay policy) and
 * must be resumed via unlock() from inside a user-gesture handler. Every
 * consumer no-ops while the context isn't running, so constructing the
 * engine at boot is always safe.
 */
export class AudioEngine {
  readonly context: AudioContext;
  readonly sfxBus: GainNode;
  readonly voiceBus: GainNode;
  readonly ambientBus: GainNode;
  private readonly masterGain: GainNode;
  private everUnlocked = false;

  constructor() {
    this.context = new AudioContext();

    this.masterGain = this.context.createGain();
    this.masterGain.gain.value = 0.8;

    // Gentle glue compressor so overlapping chirps/sfx never clip the output.
    const compressor = this.context.createDynamicsCompressor();
    compressor.threshold.value = -24;
    compressor.knee.value = 30;
    compressor.ratio.value = 6;
    compressor.attack.value = 0.003;
    compressor.release.value = 0.25;

    this.sfxBus = this.context.createGain();
    this.sfxBus.gain.value = 0.9;
    this.voiceBus = this.context.createGain();
    this.voiceBus.gain.value = 0.9;
    this.ambientBus = this.context.createGain();
    this.ambientBus.gain.value = 0.6;

    this.sfxBus.connect(this.masterGain);
    this.voiceBus.connect(this.masterGain);
    this.ambientBus.connect(this.masterGain);
    this.masterGain.connect(compressor);
    compressor.connect(this.context.destination);
  }

  /** True once unlock() has been called from a user gesture. */
  get unlocked(): boolean {
    return this.everUnlocked;
  }

  /**
   * Resume the context. MUST be called from inside a user-gesture handler
   * (pointerdown/keydown) or the browser will ignore it. Idempotent.
   */
  unlock(): void {
    this.everUnlocked = true;
    void this.context.resume().catch(() => {});
  }

  /** Park the context (visibilitychange: hidden). */
  suspend(): void {
    void this.context.suspend().catch(() => {});
  }

  /** Un-park after suspend(). Only acts if the user already unlocked audio. */
  resume(): void {
    if (!this.everUnlocked) return;
    void this.context.resume().catch(() => {});
  }

  /** Master volume, 0..1. Short ramp to avoid zipper clicks. */
  setMasterVolume(v: number): void {
    const clamped = Math.min(1, Math.max(0, v));
    this.masterGain.gain.setTargetAtTime(clamped, this.context.currentTime, 0.02);
  }
}
